import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { BodyBadge } from '@/components/BodyBadge';
import { Icon } from '@/components/Icon';
import { explainMeeting } from '@/lib/api';
import type { Meeting } from '@/lib/types';
import { can } from '@/lib/tier';
import { useTier } from '@/session/SessionProvider';
import './RecapCard.css';

/**
 * A meeting recap card.
 *
 * Two behaviors here are easy to get wrong and are spelled out in the README:
 *
 * **Aware Explain is a button, not a query.** It reads the meeting's own content, simplifies
 * it, and replaces the summary *in place*, badged "Aware Explain · simplified", with "Show
 * original" toggling back. It does not open a chat thread and it is not Ask Aware.
 *
 * **The ordinance action only renders when the item actually carries a number.** Pro tiers get
 * "Track [number]", which promotes it into Legislative History. Residents get a locked,
 * dashed "Follow [number]" that routes to pricing. An item with no spoken number gets neither
 * — the pipeline writes "N/A" and there is nothing to track.
 */

interface RecapCardProps {
  meeting: Meeting;
  /** True in the two-pane layout when this row is the one open in the detail pane. */
  selected?: boolean;
  /** Show the town name — needed on the aggregated "Latest" view, redundant on one town. */
  showTown?: boolean;
  onOpen: (meeting: Meeting) => void;
}

export function RecapCard({ meeting, selected = false, showTown = false, onOpen }: RecapCardProps) {
  const tier = useTier();
  const canTrack = can(tier, 'legislativeHistory');

  const explain = useMutation({
    mutationFn: () => explainMeeting(meeting.id),
  });

  const simplified = explain.data?.text;
  const showingSimplified = Boolean(simplified);

  return (
    <article className="recap" data-selected={selected}>
      <button type="button" className="recap__open" onClick={() => onOpen(meeting)}>
        <span className="u-sr-only">Open recap: {meeting.title}</span>
      </button>

      <div className="recap__head">
        <BodyBadge body={meeting.body} short />
        <span className="recap__where">
          {showTown ? `${meeting.townName}, ${meeting.townRegion} · ` : ''}
          {meeting.bodyName}
        </span>
        <span className="recap__ago">{meeting.ago}</span>
      </div>

      <h3 className="recap__title">{meeting.title}</h3>

      {showingSimplified && (
        <p className="recap__explainBadge">
          <Icon name="zap" /> Aware Explain · simplified
        </p>
      )}
      <p className="recap__summary">{showingSimplified ? simplified : meeting.summary}</p>

      {meeting.tags.length > 0 && (
        <ul className="recap__tags">
          {meeting.tags.map((tag) => (
            <li key={tag} className="recap__tag">
              {tag}
            </li>
          ))}
        </ul>
      )}

      <div className="recap__actions">
        {showingSimplified ? (
          <button
            type="button"
            className="recap__action"
            onClick={() => explain.reset()}
          >
            Show original
          </button>
        ) : (
          <button
            type="button"
            className="recap__action recap__action--primary"
            onClick={() => explain.mutate()}
            disabled={explain.isPending}
          >
            <Icon name="zap" />
            {explain.isPending ? 'Explaining…' : 'Aware Explain'}
          </button>
        )}

        {explain.isError && (
          <span className="recap__error" role="status">
            Couldn’t simplify this one. Try again.
          </span>
        )}

        {/* Only when the item carries a spoken ordinance or resolution number. */}
        {meeting.num &&
          (meeting.inRecord ? (
            <Link to="/legislative-history" className="recap__action recap__action--quiet">
              In this record
            </Link>
          ) : canTrack ? (
            <button type="button" className="recap__action">
              Track {meeting.num}
            </button>
          ) : (
            <Link to="/pricing" className="recap__action recap__action--locked">
              <Icon name="lock" />
              Follow {meeting.num}
            </Link>
          ))}
      </div>
    </article>
  );
}
